import styles from "./Modals.module.scss";
import React, { useEffect, useRef, useState } from "react";
import type { Destination } from "@whatslly/types/messages-server/destinations";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import { useTranslation } from "react-i18next";
import { FormCheckBox } from "@whatslly/ui-components/v2/FormElements/FormCheckBox";
import classNames from "classnames";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { observer } from "mobx-react-lite";
import { DestinationsStore } from "../DestinationsStore";
import { DestinationsModalButtons } from "../DestinationsModalButtons";
import { FormTextArea } from "@whatslly/ui-components/v2/FormElements/FormTextArea";
import { FormPasswordTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput/FormPasswordTextInput";
import { TIME_ZONE_OPTIONS } from "@whatslly/commons/timeZoneOptions";
import type { DestinationFormProps, FormDestinationDataType } from "../utils/DestinationsListTypes";
const FILENAME_TEMPLATE = '{{sourcePhone}}/{{date timestamp "YYYY/MM/DD"}}.';
const BASE_DATA: Destination = {
  type: "sftp" as const,
  integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
  settings: {
    format: "csv",
    host: "",
    port: "22",
    password: "",
    basePath: "",
    readBasePath: "",
    username: "",
    privateKey: "",
    noFolderStructureEnabled: false,
    saveFileZipped: false,
    shouldIncludeUploadTime: false,
    fileNameTimeZone: "UTC",
    filenameTemplate: `${FILENAME_TEMPLATE}csv`,
    mediaFilenameTemplate: "{{sourcePhone}}/media/{{_id}}.{{fileType}}",
  },
};
const TIME_ZONE_DROPDOWN_OPTIONS = [...TIME_ZONE_OPTIONS].map((value) => ({ value, label: value }));

type FormDataType = FormDestinationDataType<"sftp">;

export const SFTPModalForm: React.FC<DestinationFormProps<"sftp">> = observer(({ onSubmitForm, settings }) => {
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<FormDataType>>(null);
  const [formatValue, setFormatValue] = useState<"json" | "csv">(settings?.format ?? "csv");
  const [fileNameTimeZone, setFileNameTimeZone] = useState<string>(settings?.fileNameTimeZone ?? "UTC");
  const [isCredentialsError, setIsCredentialsError] = useState(false);
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);
  const validate = async (values?: FormDataType) => {
    const errors: FormikErrors<PartialRecord<keyof FormDataType, string>> = {};
    if (!values?.username) errors.username = t("field_is_required");
    if (!values?.format) errors.format = t("field_is_required");
    if (!values?.host) errors.host = t("field_is_required");
    if (values?.username) {
      if ((!values?.privateKey && !values?.password) || (values?.privateKey && values?.password)) {
        setIsCredentialsError(true);
      } else {
        setIsCredentialsError(false);
      }
    }
    if (!values?.filenameTemplate) errors.filenameTemplate = t("field_is_required");

    Object.keys(errors).length > 0 ? setFormikErrorHasErrors(true) : setFormikErrorHasErrors(false);
    return errors;
  };
  const onChangeFormat = (value: "json" | "csv") => {
    setFormatValue(value);
    formikContext.current?.setFieldValue("filenameTemplate", `${FILENAME_TEMPLATE}${value}`);
  };

  const changeFileNameUploadDateTimezone = (value: string) => {
    setFileNameTimeZone(value);
    formikContext.current?.setFieldValue("fileNameTimeZone", value);
  };

  const getFormValues = () => {
    const data = (formikContext.current?.values ?? {}) as FormDataType;
    return { ...BASE_DATA, settings: { ...data, format: formatValue, fileNameTimeZone } };
  };

  useEffect(() => {
    validate(getFormValues().settings);
  }, []);

  const onSubmit = async () => {
    store.testConnection(t, getFormValues());
    const res = await store.testConnectionPromise;
    if (res?.success) {
      onSubmitForm(getFormValues());
    }
  };

  return (
    <FormElement initialValues={settings || BASE_DATA.settings} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.format")} required />
        <FormSelect<{ value: "json" | "csv" }>
          name={"format"}
          paddingInline="none"
          options={[{ label: "CSV", value: "csv" }]}
          onChange={(selectedOption: { value: "json" | "csv" }) => onChangeFormat(selectedOption?.value)}
          defaultValue={
            settings?.format
              ? {
                  label: settings.format.toUpperCase(),
                  value: settings.format,
                }
              : { label: "CSV", value: "csv" }
          }
          isClearable={false}
          required
        />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.host")} required />
        <FormTextInput name={"host"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.save_base_path")} />
        <FormTextInput name={"basePath"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.read_base_path")} />
        <FormTextInput name={"readBasePath"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.port")} />
        <FormTextInput name={"port"} paddingInline="none" defaultValue={22} />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.username")} required />
        <FormTextInput name={"username"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.password")} />
        <FormPasswordTextInput name={"password"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.private_key")} />
        <FormTextArea name={"privateKey"} paddingInline="none" labelText="" />
      </div>
      {isCredentialsError && (
        <div className={styles.errorText} data-testid="sftp-model-certs-error-text">
          {t("destinations_page.sftp_password_or_privateKeyError")}
        </div>
      )}
      <div className={classNames(styles.field)}>
        <Label className={styles.filedLabel} text={t("destinations_page.file_name_template")} required />
        <FormTextInput name={"filenameTemplate"} paddingInline="none" />
      </div>
      <div className={classNames(styles.field, styles.fieldCheckbox)}>
        <Label className={styles.filedLabel} text={t("destinations_page.store_media")} />
        <FormCheckBox name={"storeMedia"} paddingInline="none" />
      </div>
      <div className={classNames(styles.field, styles.fieldCheckbox)}>
        <Label className={styles.filedLabel} text={t("destinations_page.file_name_upload_date")} />
        <FormCheckBox name={"shouldIncludeUploadTime"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.file_name_date_timezone")} />
        <FormSelect
          isSearchable={true}
          name={`fileNameTimeZone`}
          paddingInline="none"
          options={TIME_ZONE_DROPDOWN_OPTIONS}
          onChange={(selectedOption: (typeof TIME_ZONE_DROPDOWN_OPTIONS)[number]) => changeFileNameUploadDateTimezone(selectedOption?.value)}
          defaultValue={
            settings?.fileNameTimeZone
              ? {
                  label: settings.fileNameTimeZone,
                  value: settings.fileNameTimeZone,
                }
              : { label: "UTC", value: "UTC" }
          }
          isClearable={false}
          required
        />
      </div>
      <div className={classNames(styles.field, styles.fieldCheckbox)}>
        <Label className={styles.filedLabel} text={t("destinations_page.flat")} />
        <FormCheckBox name={"noFolderStructureEnabled"} paddingInline="none" />
      </div>
      <div className={classNames(styles.field, styles.fieldCheckbox)}>
        <Label className={styles.filedLabel} text={t("destinations_page.zipped")} />
        <FormCheckBox name={"saveFileZipped"} paddingInline="none" />
      </div>
      <div className={classNames(styles.field)}>
        <Label className={styles.filedLabel} text={t("destinations_page.media_file_name_template")} />
        <FormTextInput name={"mediaFilenameTemplate"} paddingInline="none" />
      </div>
      <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
    </FormElement>
  );
});
